import * as THREE from 'three';

import { scene, camera, renderer } from './scene.js';
import { keys } from './input.js';
import { PROP_COUNT, FOG_FAR } from './config.js';

import { createPlanet }      from './world/planet.js';
import { createAtmosphere }  from './world/atmosphere.js';
import { makeTree, makeRock, makeFlower, makeGrass, makeGlowPod } from './world/props.js';
import { placeOnPlanet }     from './world/placement.js';
import { spawnPickup, updatePickups } from './world/pickups.js';
import { scatterParts }      from './game/commParts.js';
import { registerSwayable, updateWind } from './world/wind.js';
import { createFireflies, updateFireflies } from './world/fireflies.js';
import { registerGlowPod, updateBioLights } from './world/bioLights.js';
import { createLandingPod, updateLandingPod, POD_DIRECTION } from './world/landingPod.js';
import { createPond, updatePond, POND_DIRECTION } from './world/pond.js';
import { createRuins, updateRuins, RUINS_DIRECTION } from './world/ruins.js';
import { createCave, updateCave, CAVE_DIRECTION } from './world/cave.js';
import { createCreatures, updateCreatures, getBoardCarrierPos } from './world/creatures.js';
import { initFootprints, updateFootprints } from './world/footprints.js';
import { initThrowing, updateThrowing } from './world/throwing.js';
import { initEffects, updateEffects } from './world/effects.js';

import { createStarfield, updateStars }                 from './space/stars.js';
import { createCelestialBodies, updateCelestialBodies } from './space/celestial.js';
import { createShootingStars, updateShootingStars }     from './space/shootingStars.js';
import { createMeteors, updateMeteors }                 from './space/meteors.js';

import { makeCharacter }            from './player/character.js';
import { player, updatePlayer }     from './player/controller.js';
import { updateInteractions }       from './player/interactions.js';

import { initCamera, updateCamera, getIntroProgress, snapCamera } from './camera/follow.js';

import { initDialogUI }        from './ui/dialog.js';
import { initInventoryUI }     from './ui/inventoryPanel.js';
import { initPromptUI }        from './ui/prompt.js';
import { initDevMenu }         from './ui/devMenu.js';
import { initTitle, hasStarted } from './ui/title.js';
import { initSoundtrack, startSoundtrack } from './audio/soundtrack.js';

import { initStory }        from './game/story.js';
import { initEndingCutscene, updateEndingCutscene } from './game/endingCutscene.js';
import { events }           from './core/events.js';

// ---------- UI ----------
initDialogUI();
initInventoryUI();
initPromptUI();
initSoundtrack();
initTitle(startSoundtrack);   // first key/click dismisses + starts audio

// ---------- Sky + distant things ----------
scene.add(createStarfield());
createCelestialBodies(scene);
createShootingStars(scene);
createMeteors(scene);

// ---------- Planet + props ----------
const planet = createPlanet();
scene.add(planet);
scene.add(createAtmosphere());

// Keep natural flora off the fixed POIs — any random direction within the
// cone of a POI gets rerolled so trees / rocks / grass don't grow through
// the pod, the pond, the ruins, or their immediate surroundings.
const POD_EXCLUSION_COS   = Math.cos(11 * Math.PI / 180);
const POND_EXCLUSION_COS  = Math.cos(10 * Math.PI / 180);
const RUINS_EXCLUSION_COS = Math.cos(8  * Math.PI / 180);
const CAVE_EXCLUSION_COS  = Math.cos(10 * Math.PI / 180);
// Spawn-area exclusion — a cone reaching 20° of arc ahead of the player's
// initial position + forward direction, so nothing grows directly in
// their line of sight at the start (the landing pod is supposed to be
// the first thing they notice).
const SPAWN_AHEAD_DIRECTION = new THREE.Vector3(
  Math.sin(10 * Math.PI / 180), Math.cos(10 * Math.PI / 180), 0,
).normalize();
const SPAWN_AHEAD_EXCLUSION_COS = Math.cos(12 * Math.PI / 180);
const POI_EXCLUSIONS = [
  { dir: POD_DIRECTION,         cos: POD_EXCLUSION_COS         },
  { dir: POND_DIRECTION,        cos: POND_EXCLUSION_COS        },
  { dir: RUINS_DIRECTION,       cos: RUINS_EXCLUSION_COS       },
  { dir: CAVE_DIRECTION,        cos: CAVE_EXCLUSION_COS        },
  { dir: SPAWN_AHEAD_DIRECTION, cos: SPAWN_AHEAD_EXCLUSION_COS },
];
function randomSurfaceDir() {
  const v = new THREE.Vector3();
  let ok;
  do {
    v.randomDirection();
    ok = true;
    for (const z of POI_EXCLUSIONS) {
      if (v.dot(z.dir) > z.cos) { ok = false; break; }
    }
  } while (!ok);
  return v;
}

for (let i = 0; i < PROP_COUNT; i++) {
  const dir  = randomSurfaceDir();
  const roll = Math.random();
  const prop = roll < 0.55 ? makeTree()
             : roll < 0.85 ? makeRock()
             :                makeFlower();
  placeOnPlanet(prop, dir, planet);
  scene.add(prop);
  registerSwayable(prop);   // trees / flowers bend in the breeze; rocks ignored
}

// Scatter grass tufts — small and collider-less, so they just add ground-
// level texture and something for the breeze to bend.
for (let i = 0; i < 350; i++) {
  const dir = randomSurfaceDir();
  const grass = makeGrass();
  placeOnPlanet(grass, dir, planet);
  scene.add(grass);
  registerSwayable(grass);
}

// Bioluminescent ground pods — rarer than grass, each a small cluster of
// glowing mushroom-shaped growths. Give the night surface a bit of magic.
for (let i = 0; i < 55; i++) {
  const dir = randomSurfaceDir();
  const pod = makeGlowPod();
  placeOnPlanet(pod, dir, planet);
  scene.add(pod);
  registerSwayable(pod);
  registerGlowPod(pod);
}

// Ambient motes drifting around the surface.
createFireflies(scene);

// Visual effects pool (landing rings, etc.) — listens to character events.
initEffects(scene);

// Footprint trail pool.
initFootprints(scene);

// Throwing / lure system — used by the Circuit Board puzzle.
initThrowing(scene, planet);

// ---------- Pickups ----------
const CRYSTAL_TYPES = [
  { id: 'crystal_amber', name: 'Amber Crystal', color: 0xffcf50, icon: '◆' },
  { id: 'crystal_azure', name: 'Azure Crystal', color: 0x3ae0ff, icon: '◆' },
  { id: 'crystal_rose',  name: 'Rose Crystal',  color: 0xff5aa0, icon: '◆' },
];
for (let i = 0; i < 12; i++) {
  spawnPickup(scene, planet, {
    ...CRYSTAL_TYPES[i % CRYSTAL_TYPES.length],
    dir: randomSurfaceDir(),
  });
}

// All four communicator parts are gated by puzzles now — nothing is
// scattered randomly on the surface.
const partEntries = scatterParts(
  scene, planet, randomSurfaceDir,
  ['comm_antenna', 'comm_battery', 'comm_board', 'comm_chip'],
);

// Crashed landing pod — fixed narrative landmark on the far side of the planet.
createLandingPod(scene, planet);

// Alien pond — secondary POI, a small patch of still water with lily pads.
createPond(scene, planet);

// Alien ruins — altar with three crystal sockets that release the Antenna
// Array comm part when solved.
createRuins(scene, planet);

// Cave POI — stealth puzzle with sleeping guardians protecting the
// Signal Chip on a pedestal at the back.
createCave(scene, planet);

// Alien grazers — ambient wildlife that wanders and scurries. Pod-only
// exclusion is fine (creatures may approach the pond naturally).
createCreatures(scene, planet, {
  count: 14,
  exclude: { dir: POD_DIRECTION, cos: POD_EXCLUSION_COS },
});

// ---------- Character ----------
const character = makeCharacter();
scene.add(character);


initCamera(camera, player.charPos, player.charForward);

// ---------- Dev menu (teleport) ----------
// Teleports the player to a position a short arc back from the target
// direction, facing toward it — so POIs are visible ahead rather than
// clipping through the player on arrival. Camera is snapped to avoid the
// lerp-across-the-planet flyover.
const TELEPORT_OFFSET_ARC = 0.2;   // rad; ~6.4 world units at PLANET_R=32
function teleportPlayer(dir, tangentOverride = null) {
  const poiDir = dir.clone().normalize();

  // Pick a tangent vector at the POI. If the caller supplied one (to
  // control final facing), use it projected/normalised against poiDir;
  // otherwise pick a reasonable default from world +Y (or +X at poles).
  let tangent;
  if (tangentOverride) {
    tangent = tangentOverride.clone()
      .addScaledVector(poiDir, -tangentOverride.dot(poiDir))
      .normalize();
  } else {
    const seedUp = Math.abs(poiDir.y) > 0.9
      ? new THREE.Vector3(1, 0, 0)
      : new THREE.Vector3(0, 1, 0);
    tangent = seedUp
      .addScaledVector(poiDir, -seedUp.dot(poiDir))
      .normalize();
  }

  // Rotate the POI direction by TELEPORT_OFFSET_ARC along -tangent so
  // the spawn sits behind the POI relative to that tangent.
  const c = Math.cos(TELEPORT_OFFSET_ARC);
  const s = Math.sin(TELEPORT_OFFSET_ARC);
  player.charPos
    .copy(poiDir).multiplyScalar(c)
    .addScaledVector(tangent, -s)
    .normalize();

  // Forward = (POI − charPos) projected onto the tangent plane at charPos.
  player.charForward.copy(poiDir).sub(player.charPos);
  player.charForward.addScaledVector(
    player.charPos, -player.charForward.dot(player.charPos),
  );
  if (player.charForward.lengthSq() < 1e-4) player.charForward.copy(tangent);
  player.charForward.normalize();

  player.altitude = 0;
  player.vertV = 0;
  player.curMove = 0;
  player.curTurn = 0;

  snapCamera(camera, player.charPos, player.charForward);
}

const devPois = [
  { name: 'Spawn (north pole)',  dir: new THREE.Vector3(0, 1, 0) },
  { name: 'Antipode',            dir: new THREE.Vector3(0, -1, 0) },
  { name: 'Landing Pod',         dir: POD_DIRECTION },
  { name: 'Pond',                dir: POND_DIRECTION },
  { name: 'Ruins',               dir: RUINS_DIRECTION },
  // The cave entrance is at cave-local +X. The tangent we want to face
  // along is cave-local −X (into the cave) in world coordinates; the
  // teleport places us opposite that tangent (outside the entrance)
  // and faces us toward the POI, so we end up standing just outside
  // the archway looking directly in.
  {
    name: 'Cave',
    dir:  CAVE_DIRECTION,
    tangent: (() => {
      const caveQuat = new THREE.Quaternion().setFromUnitVectors(
        new THREE.Vector3(0, 1, 0), CAVE_DIRECTION,
      );
      return new THREE.Vector3(-1, 0, 0).applyQuaternion(caveQuat);
    })(),
  },
  // Queries the carrier each click, so the teleport tracks the creature
  // as it wanders. Falls back to a random spot once the board is taken.
  { name: 'Board Carrier',
    dir: () => getBoardCarrierPos() ?? new THREE.Vector3().randomDirection() },
  ...partEntries.map(({ name, entry }) => ({
    name: `Part · ${name}`,
    dir: entry.worldPos.clone(),
  })),
  { name: 'Random spot', dir: () => new THREE.Vector3().randomDirection() },
];

const devActions = [
  { name: 'Trigger Ending', fn: () => events.emit('rescue:arriving') },
];

initDevMenu({ pois: devPois, onTeleport: teleportPlayer, actions: devActions });

// During the space-to-surface intro, widen the fog so the planet is visible
// from orbit. The loop interpolates it back to FOG_FAR as the camera descends.
const INTRO_FOG_FAR = 2000;
scene.fog.far = INTRO_FOG_FAR;

// ---------- Narrative ----------
initStory();
initEndingCutscene(scene, planet, character);

// ---------- Loop ----------
// While the title screen is visible we still render (so the scene is
// the backdrop behind it), but skip all game-state updates so the
// intro camera sweep and story beats begin from a clean start when
// the player actually presses a key.
let last = performance.now();
function loop(now) {
  const dt = Math.min((now - last) / 1000, 0.05);
  last = now;

  if (hasStarted()) {
    updatePlayer(dt, keys, character);
    updateInteractions(character);
    updateFootprints(player.charPos, player.charForward, player.altitude, planet);
    updateLandingPod(character);
    updatePond(character);
    updateRuins(character);
    updateCave(character);
    updateThrowing(dt);
    updateCreatures(dt, character.position);
    updatePickups();
    updateWind();
    updateFireflies();
    updateBioLights();
    updateEffects(dt);
    updateEndingCutscene(dt);
    updateStars(now / 1000);
    updateShootingStars(dt);
    updateMeteors(dt);
    updateCelestialBodies(dt);
    updateCamera(camera, player.charPos, player.charForward, player.altitude, dt);

    // Pull fog in from the intro-wide setting to the normal one as the
    // camera descends. Once the intro is done, getIntroProgress()
    // stays at 1 so fog stays at FOG_FAR.
    const introP = getIntroProgress();
    scene.fog.far = INTRO_FOG_FAR + (FOG_FAR - INTRO_FOG_FAR) * introP;
  }

  renderer.render(scene, camera);
  requestAnimationFrame(loop);
}
requestAnimationFrame(loop);
